import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchFolderTree,
  fetchFolders,
  fetchFolderById,
  createFolder,
  updateFolder,
  moveFolder,
  deleteFolder,
  fetchScenariosInFolder,
  fetchRootScenarios,
  fetchAllScenariosInRelease,
  moveScenarioToFolder,
  createTestScenario,
  updateTestScenarioInFolder,
  deleteTestScenario,
  reorderScenarios,
  reorderFolders,
  cloneFolder,
  cloneTestScenario
} from '../../api/folderApi'

// ============================================================================
// ASYNC THUNKS - FOLDERS
// ============================================================================

/**
 * FETCH FOLDER TREE
 * Fetches hierarchical folder tree for a release
 */
export const fetchFolderTreeAsync = createAsyncThunk(
  'folders/fetchFolderTree',
  async (releaseId, { rejectWithValue }) => {
    try {
      return await fetchFolderTree(releaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch folder tree',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH FOLDERS (flat list)
 */
export const fetchFoldersAsync = createAsyncThunk(
  'folders/fetchFolders',
  async (releaseId, { rejectWithValue }) => {
    try {
      return await fetchFolders(releaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch folders',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH SINGLE FOLDER
 */
export const fetchFolderByIdAsync = createAsyncThunk(
  'folders/fetchFolderById',
  async (folderId, { rejectWithValue }) => {
    try {
      return await fetchFolderById(folderId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * CREATE FOLDER
 */
export const createFolderAsync = createAsyncThunk(
  'folders/createFolder',
  async (folderData, { rejectWithValue }) => {
    try {
      return await createFolder(folderData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * UPDATE FOLDER
 */
export const updateFolderAsync = createAsyncThunk(
  'folders/updateFolder',
  async ({ folderId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateFolder(folderId, updates)
      return { folderId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * MOVE FOLDER
 */
export const moveFolderAsync = createAsyncThunk(
  'folders/moveFolder',
  async ({ folderId, moveData }, { rejectWithValue }) => {
    try {
      const data = await moveFolder(folderId, moveData)
      return { folderId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to move folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * DELETE FOLDER
 */
export const deleteFolderAsync = createAsyncThunk(
  'folders/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      await deleteFolder(folderId)
      return { folderId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete folder',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// ASYNC THUNKS - SCENARIOS
// ============================================================================

/**
 * FETCH SCENARIOS IN FOLDER
 */
export const fetchScenariosInFolderAsync = createAsyncThunk(
  'folders/fetchScenariosInFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      const data = await fetchScenariosInFolder(folderId)
      return { folderId, scenarios: data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH ROOT SCENARIOS
 * Scenarios not in any folder
 */
export const fetchRootScenariosAsync = createAsyncThunk(
  'folders/fetchRootScenarios',
  async (releaseId, { rejectWithValue }) => {
    try {
      return await fetchRootScenarios(releaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch root scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH ALL SCENARIOS IN RELEASE
 */
export const fetchAllScenariosAsync = createAsyncThunk(
  'folders/fetchAllScenarios',
  async (releaseId, { rejectWithValue }) => {
    try {
      return await fetchAllScenariosInRelease(releaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch all scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * MOVE SCENARIO TO FOLDER
 */
export const moveScenarioToFolderAsync = createAsyncThunk(
  'folders/moveScenarioToFolder',
  async ({ testScenarioId, moveData }, { rejectWithValue }) => {
    try {
      const data = await moveScenarioToFolder(testScenarioId, moveData)
      return { testScenarioId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to move scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * CREATE TEST SCENARIO
 */
export const createTestScenarioAsync = createAsyncThunk(
  'folders/createTestScenario',
  async (scenarioData, { rejectWithValue }) => {
    try {
      return await createTestScenario(scenarioData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * UPDATE TEST SCENARIO
 */
export const updateTestScenarioAsync = createAsyncThunk(
  'folders/updateTestScenario',
  async ({ testScenarioId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateTestScenarioInFolder(testScenarioId, updates)
      return { testScenarioId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * DELETE TEST SCENARIO
 */
export const deleteTestScenarioAsync = createAsyncThunk(
  'folders/deleteTestScenario',
  async (testScenarioId, { rejectWithValue }) => {
    try {
      await deleteTestScenario(testScenarioId)
      return { testScenarioId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * REORDER SCENARIOS
 * Updates orderIndex for multiple scenarios in a folder
 */
export const reorderScenariosAsync = createAsyncThunk(
  'folders/reorderScenarios',
  async (reorderData, { rejectWithValue }) => {
    try {
      await reorderScenarios(reorderData)
      return reorderData
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to reorder scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * REORDER FOLDERS
 * Updates orderIndex for multiple folders within a parent
 */
export const reorderFoldersAsync = createAsyncThunk(
  'folders/reorderFolders',
  async (reorderData, { rejectWithValue }) => {
    try {
      await reorderFolders(reorderData)
      return reorderData
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to reorder folders',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// ASYNC THUNKS - CLONE OPERATIONS
// ============================================================================

/**
 * CLONE FOLDER
 * Creates a deep copy of a folder with all its contents (sub-folders and scenarios)
 */
export const cloneFolderAsync = createAsyncThunk(
  'folders/cloneFolder',
  async ({ folderId, targetReleaseId }, { rejectWithValue }) => {
    try {
      return await cloneFolder(folderId, targetReleaseId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to clone folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * CLONE TEST SCENARIO
 * Creates a deep copy of a test scenario with all test cases and steps
 */
export const cloneTestScenarioAsync = createAsyncThunk(
  'folders/cloneTestScenario',
  async (testScenarioId, { rejectWithValue }) => {
    try {
      return await cloneTestScenario(testScenarioId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to clone scenario',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  // Hierarchical folder tree for the current release
  folderTree: [],

  // Flat list of all folders (for quick lookups)
  folders: [],

  // Currently selected folder ID (null initially, defaults to "Home" folder on load)
  selectedFolderId: null,

  // Scenarios for the selected folder
  scenarios: [],

  // All scenarios in the release (for "All Scenarios" view)
  allScenarios: [],

  // Expanded folder IDs in the tree (for collapse/expand state)
  expandedFolderIds: [],

  // Loading states
  foldersLoading: false,
  scenariosLoading: false,

  // Error state
  error: null
}

// ============================================================================
// SLICE
// ============================================================================

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    /**
     * Select a folder (shows its scenarios in the right panel)
     */
    selectFolder: (state, action) => {
      // Only clear scenarios if selecting a DIFFERENT folder
      // This prevents scenarios from disappearing when re-clicking the same folder
      if (state.selectedFolderId !== action.payload) {
        state.selectedFolderId = action.payload
        // Clear scenarios when selecting a new folder (will be fetched)
        state.scenarios = []
      }
    },

    /**
     * Toggle folder expand/collapse in the tree
     */
    toggleFolderExpand: (state, action) => {
      const folderId = action.payload
      if (state.expandedFolderIds.includes(folderId)) {
        state.expandedFolderIds = state.expandedFolderIds.filter(id => id !== folderId)
      } else {
        state.expandedFolderIds.push(folderId)
      }
    },

    /**
     * Set expanded folder IDs (for restoring from localStorage)
     */
    setExpandedFolderIds: (state, action) => {
      state.expandedFolderIds = action.payload
    },

    /**
     * Clear folders state (when navigating away from release)
     */
    clearFolders: (state) => {
      state.folderTree = []
      state.folders = []
      state.selectedFolderId = null
      state.scenarios = []
      state.allScenarios = []
      state.error = null
    },

    /**
     * Clear error state
     */
    clearFoldersError: (state) => {
      state.error = null
    },

    /**
     * Optimistically add a folder to the tree
     */
    addFolderOptimistic: (state, action) => {
      const newFolder = action.payload
      state.folders.push(newFolder)
      // Add to tree (simplified - full tree rebuild would be needed for nested)
      if (!newFolder.parentId) {
        state.folderTree.push({ ...newFolder, children: [] })
      }
    },

    /**
     * Optimistically remove a folder from the tree
     */
    removeFolderOptimistic: (state, action) => {
      const folderId = action.payload
      state.folders = state.folders.filter(f => f.id !== folderId)
      state.folderTree = state.folderTree.filter(f => f.id !== folderId)
      // If deleted folder was selected, clear selection
      if (state.selectedFolderId === folderId) {
        state.selectedFolderId = null
        state.scenarios = []
      }
    },

    /**
     * Optimistically reorder scenarios (before API call)
     */
    reorderScenariosOptimistic: (state, action) => {
      const reorderData = action.payload // Array of { id, orderIndex }
      // Create a map for quick lookup
      const orderMap = new Map(reorderData.map(item => [item.id, item.orderIndex]))

      // Update orderIndex in scenarios list
      state.scenarios = state.scenarios.map(scenario => ({
        ...scenario,
        orderIndex: orderMap.has(scenario.id) ? orderMap.get(scenario.id) : scenario.orderIndex
      }))

      // Sort by orderIndex
      state.scenarios.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))

      // Also update in allScenarios
      state.allScenarios = state.allScenarios.map(scenario => ({
        ...scenario,
        orderIndex: orderMap.has(scenario.id) ? orderMap.get(scenario.id) : scenario.orderIndex
      }))
    }
  },
  extraReducers: (builder) => {
    builder
      // ====================================================================
      // FETCH FOLDER TREE
      // ====================================================================
      .addCase(fetchFolderTreeAsync.pending, (state) => {
        state.foldersLoading = true
        state.error = null
      })
      .addCase(fetchFolderTreeAsync.fulfilled, (state, action) => {
        state.foldersLoading = false
        state.folderTree = action.payload || []
        // Also flatten for quick lookups
        const flatten = (folders) => {
          let result = []
          folders.forEach(folder => {
            result.push(folder)
            if (folder.children && folder.children.length > 0) {
              result = result.concat(flatten(folder.children))
            }
          })
          return result
        }
        state.folders = flatten(state.folderTree)
        state.error = null
      })
      .addCase(fetchFolderTreeAsync.rejected, (state, action) => {
        state.foldersLoading = false
        state.error = action.payload?.message || 'Failed to fetch folders'
      })

      // ====================================================================
      // FETCH FOLDERS (flat)
      // ====================================================================
      .addCase(fetchFoldersAsync.fulfilled, (state, action) => {
        state.folders = action.payload || []
      })

      // ====================================================================
      // CREATE FOLDER
      // ====================================================================
      .addCase(createFolderAsync.fulfilled, (state, action) => {
        const newFolder = action.payload
        state.folders.push(newFolder)
        // Add to tree at appropriate location
        if (!newFolder.parentId) {
          state.folderTree.push({ ...newFolder, children: [] })
        } else {
          // Find parent and add as child (recursive function needed for deep nesting)
          const addToParent = (folders) => {
            for (let folder of folders) {
              if (folder.id === newFolder.parentId) {
                if (!folder.children) folder.children = []
                folder.children.push({ ...newFolder, children: [] })
                return true
              }
              if (folder.children && addToParent(folder.children)) {
                return true
              }
            }
            return false
          }
          addToParent(state.folderTree)
        }
      })
      .addCase(createFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // UPDATE FOLDER
      // ====================================================================
      .addCase(updateFolderAsync.fulfilled, (state, action) => {
        const { folderId, data } = action.payload
        // Update in flat list
        const index = state.folders.findIndex(f => f.id === folderId)
        if (index !== -1) {
          state.folders[index] = { ...state.folders[index], ...data }
        }
        // Update in tree (recursive)
        const updateInTree = (folders) => {
          for (let folder of folders) {
            if (folder.id === folderId) {
              Object.assign(folder, data)
              return true
            }
            if (folder.children && updateInTree(folder.children)) {
              return true
            }
          }
          return false
        }
        updateInTree(state.folderTree)
      })
      .addCase(updateFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // DELETE FOLDER
      // ====================================================================
      .addCase(deleteFolderAsync.fulfilled, (state, action) => {
        const { folderId } = action.payload
        // Remove from flat list
        state.folders = state.folders.filter(f => f.id !== folderId)
        // Remove from tree (recursive)
        const removeFromTree = (folders) => {
          const index = folders.findIndex(f => f.id === folderId)
          if (index !== -1) {
            folders.splice(index, 1)
            return true
          }
          for (let folder of folders) {
            if (folder.children && removeFromTree(folder.children)) {
              return true
            }
          }
          return false
        }
        removeFromTree(state.folderTree)
        // Clear selection if deleted folder was selected
        if (state.selectedFolderId === folderId) {
          state.selectedFolderId = null
          state.scenarios = []
        }
      })
      .addCase(deleteFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // FETCH SCENARIOS IN FOLDER
      // ====================================================================
      .addCase(fetchScenariosInFolderAsync.pending, (state) => {
        state.scenariosLoading = true
      })
      .addCase(fetchScenariosInFolderAsync.fulfilled, (state, action) => {
        state.scenariosLoading = false
        state.scenarios = action.payload.scenarios || []
      })
      .addCase(fetchScenariosInFolderAsync.rejected, (state, action) => {
        state.scenariosLoading = false
        state.error = action.payload?.message || 'Failed to fetch scenarios'
      })

      // ====================================================================
      // FETCH ALL SCENARIOS
      // ====================================================================
      .addCase(fetchAllScenariosAsync.pending, (state) => {
        state.scenariosLoading = true
      })
      .addCase(fetchAllScenariosAsync.fulfilled, (state, action) => {
        state.scenariosLoading = false
        state.allScenarios = action.payload || []
        // If "All" is selected, show these scenarios
        if (state.selectedFolderId === null) {
          state.scenarios = state.allScenarios
        }
      })
      .addCase(fetchAllScenariosAsync.rejected, (state, action) => {
        state.scenariosLoading = false
        state.error = action.payload?.message || 'Failed to fetch all scenarios'
      })

      // ====================================================================
      // CREATE TEST SCENARIO
      // ====================================================================
      .addCase(createTestScenarioAsync.fulfilled, (state, action) => {
        const newScenario = action.payload
        // Add to scenarios list if in current folder
        if (state.selectedFolderId === newScenario.folderId ||
          (state.selectedFolderId === null && !newScenario.folderId)) {
          state.scenarios.push(newScenario)
        }
        // Always add to allScenarios
        state.allScenarios.push(newScenario)
      })
      .addCase(createTestScenarioAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // UPDATE TEST SCENARIO
      // ====================================================================
      .addCase(updateTestScenarioAsync.fulfilled, (state, action) => {
        const { testScenarioId, data } = action.payload
        // Update in scenarios list
        const index = state.scenarios.findIndex(s => s.id === testScenarioId)
        if (index !== -1) {
          state.scenarios[index] = { ...state.scenarios[index], ...data }
        }
        // Update in allScenarios
        const allIndex = state.allScenarios.findIndex(s => s.id === testScenarioId)
        if (allIndex !== -1) {
          state.allScenarios[allIndex] = { ...state.allScenarios[allIndex], ...data }
        }
      })
      .addCase(updateTestScenarioAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // DELETE TEST SCENARIO
      // ====================================================================
      .addCase(deleteTestScenarioAsync.fulfilled, (state, action) => {
        const { testScenarioId } = action.payload
        state.scenarios = state.scenarios.filter(s => s.id !== testScenarioId)
        state.allScenarios = state.allScenarios.filter(s => s.id !== testScenarioId)
      })
      .addCase(deleteTestScenarioAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // MOVE SCENARIO TO FOLDER
      // ====================================================================
      .addCase(moveScenarioToFolderAsync.fulfilled, (state, action) => {
        const { testScenarioId, data } = action.payload
        // Update folder assignment in allScenarios
        const allIndex = state.allScenarios.findIndex(s => s.id === testScenarioId)
        if (allIndex !== -1) {
          state.allScenarios[allIndex] = { ...state.allScenarios[allIndex], ...data }
        }
        // If scenario moved out of current folder, remove from scenarios
        const index = state.scenarios.findIndex(s => s.id === testScenarioId)
        if (index !== -1 && data.folderId !== state.selectedFolderId) {
          state.scenarios.splice(index, 1)
        }
      })
      .addCase(moveScenarioToFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // CLONE FOLDER
      // ====================================================================
      .addCase(cloneFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // CLONE TEST SCENARIO
      // ====================================================================
      .addCase(cloneTestScenarioAsync.fulfilled, (state, action) => {
        const clonedScenario = action.payload
        // Add to scenarios list if in current folder or "All Scenarios" view
        if (state.selectedFolderId === clonedScenario.folderId ||
          (state.selectedFolderId === null && !clonedScenario.folderId)) {
          state.scenarios.push(clonedScenario)
        }
        // Always add to allScenarios
        state.allScenarios.push(clonedScenario)
      })
      .addCase(cloneTestScenarioAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })
  }
})

// ============================================================================
// ACTIONS
// ============================================================================

export const {
  selectFolder,
  toggleFolderExpand,
  setExpandedFolderIds,
  clearFolders,
  clearFoldersError,
  addFolderOptimistic,
  removeFolderOptimistic,
  reorderScenariosOptimistic
} = folderSlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Select folder tree (hierarchical)
 */
export const selectFolderTree = (state) => state.folders?.folderTree || []

/**
 * Select all folders (flat list)
 */
export const selectFolders = (state) => state.folders?.folders || []

/**
 * Select currently selected folder ID
 */
export const selectSelectedFolderId = (state) => state.folders?.selectedFolderId ?? null

/**
 * Select scenarios for the current folder
 */
export const selectScenarios = (state) => state.folders?.scenarios || []

/**
 * Select all scenarios in the release
 */
export const selectAllScenarios = (state) => state.folders?.allScenarios || []

/**
 * Select expanded folder IDs
 */
export const selectExpandedFolderIds = (state) => state.folders?.expandedFolderIds || []

/**
 * Select folders loading state
 */
export const selectFoldersLoading = (state) => state.folders?.foldersLoading || false

/**
 * Select scenarios loading state
 */
export const selectScenariosLoading = (state) => state.folders?.scenariosLoading || false

/**
 * Select error state
 */
export const selectFoldersError = (state) => state.folders?.error || null

/**
 * Select the currently selected folder object
 */
export const selectCurrentFolder = (state) => {
  const folderId = state.folders?.selectedFolderId
  if (folderId === null || folderId === undefined) return null
  return (state.folders?.folders || []).find(f => f.id === folderId) || null
}

export default folderSlice.reducer
